import { useMutation, useQuery } from "@tanstack/react-query";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";

import { queryClient } from "@/config/react-query";

import createId from "@/lib/create-id";

import type { ChatEmailSummary } from "@/types/chat";
import type {
  ComposeEmailPayload,
  CreateDraftPayload,
  EmailContact,
  EmailMessage,
  EmailParticipant,
  EmailThread,
  EmailThreadListItem,
  EmailThreadScope,
  ForwardEmailPayload,
  MessageAttachmentUrls,
  ReplyEmailPayload,
  UpdateDraftPayload,
} from "@/types/email";
import {
  EmailContactType,
  EmailDirection,
  EmailParticipantRole,
} from "@/types/enums/email";
import type { CursorPage } from "@/types/pagination";

import {
  composeEmail,
  createDraft,
  deleteDraft,
  forwardEmail,
  getAttachmentDownloadUrl,
  getClinicFolderCounts,
  getEmailHeader,
  getEmailSignature,
  getEmailThread,
  getMessageAttachmentUrls,
  replyToThread,
  saveEmailHeader,
  saveEmailSignature,
  searchEmailContacts,
  sendDraft,
  updateDraft,
} from "./email";
import type {
  SaveEmailHeaderPayload,
  SaveEmailSignaturePayload,
} from "./email";
import { invalidateOpportunityContactViews } from "@/api/provider/opportunities/opportunity-query-keys";
import { phlebotomyOrderQueryKeys } from "@/api/provider/phlebotomy-order/phlebotomy-orders-query";
import {
  rollbackChatDetail,
  rollbackChatInInfiniteList,
  updateChatDetail,
  updateChatOptimistically,
  updateOrAddEmail,
} from "@/query/comms/chat";

export const emailQueryKeys = {
  all: ["email"] as const,
  // The clinic mailbox renders a thread with the clinic mailbox contact swapped in, so
  // its detail is cached under a scope-suffixed key rather than sharing the provider
  // entry. Calling this without a scope yields the provider key, which is also the
  // prefix of the clinic one — so it doubles as the non-exact key that matches both.
  thread: (threadId: string, scope: EmailThreadScope = "provider") =>
    scope === "provider"
      ? ([...emailQueryKeys.all, "thread", threadId] as const)
      : ([...emailQueryKeys.all, "thread", threadId, scope] as const),
  attachmentUrls: (messageId: string) =>
    [...emailQueryKeys.all, "attachment-urls", messageId] as const,
  attachmentDownload: (attachmentId: string) =>
    [...emailQueryKeys.all, "attachment-download", attachmentId] as const,
  signature: [...["email"], "signature"] as const,
  header: [...["email"], "header"] as const,
  contactSearch: (term: string) =>
    [...emailQueryKeys.all, "contact-search", term] as const,
};

export const emailQueryOptions = {
  thread: (threadId: string, scope: EmailThreadScope = "provider") => ({
    queryKey: emailQueryKeys.thread(threadId, scope),
    queryFn: () => getEmailThread(threadId, scope),
    select: (data: EmailThread | null) => data ?? undefined,
    enabled: !!threadId,
  }),
  attachmentDownload: (attachmentId: string) => ({
    queryKey: emailQueryKeys.attachmentDownload(attachmentId),
    queryFn: async () => {
      const response = await getAttachmentDownloadUrl(attachmentId);
      return response?.signedUrl ?? null;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes (URLs expire in 15)
    enabled: !!attachmentId,
  }),
  contactSearch: (term: string) => ({
    queryKey: emailQueryKeys.contactSearch(term),
    queryFn: () => searchEmailContacts(term),
    select: (data: EmailContact[] | null) => data ?? [],
  }),
  // Clinic-mailbox folder badge counts. Shares the mailbox's cache key so the
  // sidebar nav badge and the folder rail stay in sync and invalidate together.
  clinicFolderCounts: () => ({
    queryKey: ["email", "clinic-mailbox", "counts"] as const,
    queryFn: getClinicFolderCounts,
  }),
};

/**
 * Hook to fetch bulk attachment URLs for a message.
 * Primes the individual attachment download cache so downloads don't need
 * another API call.
 */
export function useMessageAttachmentUrls(
  messageId: string,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: emailQueryKeys.attachmentUrls(messageId),
    queryFn: async () => {
      const data = await getMessageAttachmentUrls(messageId);
      if (!data) return null;

      // Prime individual attachment download caches
      // byAttachmentId contains all attachments including inline ones
      for (const [attachmentId, signedUrl] of Object.entries(
        data.byAttachmentId,
      )) {
        queryClient.setQueryData(
          emailQueryKeys.attachmentDownload(attachmentId),
          signedUrl,
        );
      }

      return data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    // Smart refetch before URLs expire (1 minute buffer)
    refetchInterval: (query) => {
      const data = query.state.data as MessageAttachmentUrls | null | undefined;
      if (!data?.expiresAt) return false;

      const expiresAt = new Date(data.expiresAt).getTime();
      const now = Date.now();
      const bufferMs = 60 * 1000; // 1 minute buffer
      const timeUntilRefetch = expiresAt - now - bufferMs;

      // If already expired or about to expire, refetch immediately
      if (timeUntilRefetch <= 0) return 1000;

      return timeUntilRefetch;
    },
    enabled: options.enabled !== false && !!messageId,
  });
}

/**
 * Derives FROM/TO participants for an optimistic reply from the thread's
 * existing messages. For a clinic reply the FROM is the clinic address
 * (found as FROM on a previous outbound, or TO on the latest inbound)
 * and TO is the original external sender.
 */
function buildReplyParticipants(
  thread: EmailThread | undefined,
): EmailParticipant[] {
  if (!thread?.messages.length) return [];

  // Find the last inbound message — its FROM is the external sender,
  // its TO is the clinic address
  const lastInbound = [...thread.messages]
    .reverse()
    .find((m) => m.direction === "INBOUND");

  if (lastInbound) {
    const externalFrom = lastInbound.participants.find(
      (p) => p.role === "FROM",
    );
    const clinicTo = lastInbound.participants.find((p) => p.role === "TO");
    const participants: EmailParticipant[] = [];
    if (clinicTo)
      participants.push({ ...clinicTo, role: EmailParticipantRole.FROM });
    if (externalFrom)
      participants.push({ ...externalFrom, role: EmailParticipantRole.TO });
    return participants;
  }

  // Fallback: copy FROM from the last outbound message
  const lastOutbound = [...thread.messages]
    .reverse()
    .find((m) => m.direction === "OUTBOUND");
  if (lastOutbound) {
    return lastOutbound.participants.filter(
      (p) => p.role === "FROM" || p.role === "TO",
    );
  }

  return [];
}

/**
 * Predicates for the mailbox's refinement filters, keyed by the EmailFilterChip
 * values (see comms/email/types.ts). The clinic-mailbox list key is
 * ["email","clinic-mailbox", folder, search, activeFilters, sentBy], so a cached list's chips
 * tell us whether a freshly-patched row still belongs in it. An unrecognized chip
 * counts as matching, keeping the old patch-in-place behavior.
 */
const CLINIC_MAILBOX_FILTER_PREDICATES: Record<
  string,
  (item: EmailThreadListItem) => boolean
> = {
  unread: (item) => !item.isRead,
  starred: (item) => item.isStarred,
  "has-attachment": (item) => item.hasAttachments,
  patients: (item) => item.primaryPatientId !== null,
};

function rowMatchesListFilters(
  item: EmailThreadListItem,
  activeFilters: readonly unknown[],
): boolean {
  return activeFilters.every((chip) =>
    typeof chip === "string"
      ? (CLINIC_MAILBOX_FILTER_PREDICATES[chip]?.(item) ?? true)
      : true,
  );
}

/**
 * Patch a clinic-mailbox thread-list row from a server-returned thread list-item — the
 * response-driven alternative to invalidating + refetching (mirrors patchPatientDocList /
 * fax's list patching). The cache holds an InfiniteData of CursorPage<EmailThreadListItem>.
 *
 * For a row already in a list: it's replaced where it still belongs and dropped where a
 * chip no longer matches (a reply that read the thread falls out of an Unread list),
 * instead of lingering as a row that contradicts the filter.
 *
 * `insertFolders` names folders whose membership the action *gains* — a reply gives an
 * inbound-only thread its first sent message, so it now belongs in Sent (and All). The
 * returned row is inserted at the top of those cached lists when absent (a reply is the
 * thread's newest message, so it sorts to the top by last_message_date desc), which keeps
 * the just-sent reply from hiding until an unrelated refetch. Insertion is limited to
 * unfiltered, unsearched lists: a chip- or search-scoped (or patient-grouped) list has an
 * ordering we can't place a single row into correctly, so those refetch instead. A list
 * narrowed to one sender also takes the row when the reply is theirs (the row's sentBy is
 * the newest send — this reply); any other sender-filtered list missing the row is marked
 * stale, since the reply may have added it there too. A row already in a sender-filtered
 * list always stays: the thread still contains that sender's email.
 *
 * Lists are visited through the query cache rather than setQueriesData because the updater
 * needs each list's key to read its folder/chips. The ["email","clinic-mailbox"] prefix
 * also matches the folder-counts query (a different shape), so skip anything without
 * `pages`.
 */
function patchClinicMailboxRow(
  item: EmailThreadListItem,
  { insertFolders = [] }: { insertFolders?: string[] } = {},
): void {
  const cachedLists = queryClient
    .getQueryCache()
    .findAll({ queryKey: ["email", "clinic-mailbox"] });

  for (const { queryKey } of cachedLists) {
    // ["email","clinic-mailbox", folder, search, activeFilters, sentBy]
    const folder = queryKey[2];
    const search = queryKey[3];
    const activeFilters = queryKey[4];
    const sentBy = queryKey[5];
    const matchesFilters =
      !Array.isArray(activeFilters) ||
      rowMatchesListFilters(item, activeFilters);
    const canInsert =
      typeof folder === "string" &&
      insertFolders.includes(folder) &&
      (search === undefined || search === "") &&
      Array.isArray(activeFilters) &&
      activeFilters.length === 0 &&
      (sentBy == null || sentBy === item.sentBy?.id);

    // A reply can give a thread its first send by someone, adding it to that
    // person's sender-filtered list in any folder (Inbox, Starred, …). Which folders
    // it now matches, and where it sorts among a filtered list's rows, can't be told
    // client-side, so a sender-filtered list that lacks the row is marked stale —
    // refetched now if it's on screen, otherwise the next time it's shown.
    if (insertFolders.length > 0 && sentBy != null && !canInsert) {
      const cached =
        queryClient.getQueryData<
          InfiniteData<CursorPage<EmailThreadListItem> | null>
        >(queryKey);
      const present = cached?.pages?.some((page) =>
        page?.data.some((thread) => thread.id === item.id),
      );
      if (cached?.pages && !present) {
        void queryClient.invalidateQueries({ queryKey, exact: true });
        continue;
      }
    }

    queryClient.setQueryData<
      InfiniteData<CursorPage<EmailThreadListItem> | null>
    >(queryKey, (old) => {
      if (!old?.pages) return old;
      const present = old.pages.some((page) =>
        page?.data.some((thread) => thread.id === item.id),
      );

      if (!present) {
        if (!canInsert) return old;
        // Prepend to the first page — the reply is the newest message in this folder.
        return {
          ...old,
          pages: old.pages.map((page, index) =>
            page && index === 0
              ? { ...page, data: [item, ...page.data] }
              : page,
          ),
        };
      }

      return {
        ...old,
        pages: old.pages.map((page) =>
          page
            ? {
                ...page,
                data: matchesFilters
                  ? page.data.map((thread) =>
                      thread.id === item.id ? item : thread,
                    )
                  : page.data.filter((thread) => thread.id !== item.id),
              }
            : page,
        ),
      };
    });
  }
}

/** Context captured by useReplyToThread.onMutate, replayed to undo the optimistic reply. */
interface ReplyRollbackContext {
  previousThread: EmailThread | undefined;
  previousDetail: Parameters<typeof rollbackChatDetail>[1] | undefined;
  previousInfiniteListData:
    | Parameters<typeof rollbackChatInInfiniteList>[0]
    | undefined;
}

/**
 * Undo the optimistic SENDING reply in the thread cache and, for a chat-window reply, in
 * the chat caches. Shared by onError and by the two failure shapes that reach onSuccess.
 */
function rollbackOptimisticReply(
  threadId: string,
  scope: EmailThreadScope,
  chatId: string | undefined,
  context: ReplyRollbackContext,
): void {
  if (context.previousThread) {
    queryClient.setQueryData<EmailThread>(
      emailQueryKeys.thread(threadId, scope),
      context.previousThread,
    );
  }

  if (!chatId || !context.previousInfiniteListData) return;

  rollbackChatDetail(chatId, context.previousDetail);
  rollbackChatInInfiniteList(context.previousInfiniteListData);
}

/**
 * Mark a thread read in *every* cached clinic-mailbox list, not just the one on screen —
 * otherwise switching folders serves a stale cache that still shows the thread unread for
 * the rest of the stale window.
 *
 * The list the user is looking at keeps the row even when it's filtered to Unread: they're
 * reading that thread right now, and pulling the row out from under the open reader is
 * worse than briefly showing a read row in an Unread view. Every other cached Unread list
 * drops it, since a read thread no longer belongs there.
 */
export function markClinicMailboxRowRead(
  client: QueryClient,
  threadId: string,
  activeKey: readonly unknown[],
): void {
  const activeKeyId = JSON.stringify(activeKey);
  const cachedLists = client
    .getQueryCache()
    .findAll({ queryKey: ["email", "clinic-mailbox"] });

  for (const { queryKey } of cachedLists) {
    // ["email","clinic-mailbox", folder, search, activeFilters, sentBy]
    const activeFilters = queryKey[4];
    // isRead is the only row field the chips key on that this changes, so Unread is the
    // only filter a newly-read row can fall out of.
    const keepRow =
      JSON.stringify(queryKey) === activeKeyId ||
      !Array.isArray(activeFilters) ||
      !activeFilters.includes("unread");

    client.setQueryData<InfiniteData<CursorPage<EmailThreadListItem> | null>>(
      queryKey,
      (old) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) =>
            page
              ? {
                  ...page,
                  data: keepRow
                    ? page.data.map((thread) =>
                        thread.id === threadId
                          ? { ...thread, isRead: true }
                          : thread,
                      )
                    : page.data.filter((thread) => thread.id !== threadId),
                }
              : page,
          ),
        };
      },
    );
  }
}

export function useReplyToThread() {
  return useMutation({
    mutationFn: ({
      threadId,
      payload,
      files,
    }: {
      threadId: string;
      chatId?: string;
      scope?: EmailThreadScope;
      payload: ReplyEmailPayload;
      files?: File[];
    }) => replyToThread(threadId, payload, files),

    onMutate: async ({ threadId, chatId, scope = "provider", payload }) => {
      const tempId = createId("eml");
      const threadKey = emailQueryKeys.thread(threadId, scope);

      // Cancel outgoing thread query
      await queryClient.cancelQueries({
        queryKey: threadKey,
      });

      // Save previous thread cache
      const previousThread = queryClient.getQueryData<EmailThread>(threadKey);

      // Read subject from thread cache
      const subject = previousThread?.subject ?? null;

      // Derive reply participants from thread's existing messages
      // For a reply: FROM = clinic email, TO = original sender
      const derivedParticipants = buildReplyParticipants(previousThread);

      // When the user edited the To recipients, reflect them in the optimistic
      // message: keep the derived FROM, but rebuild TO from the overrides.
      const replyParticipants: EmailParticipant[] = payload.toOverride?.length
        ? [
            ...derivedParticipants.filter((p) => p.role === "FROM"),
            ...payload.toOverride.map(
              (email): EmailParticipant => ({
                id: createId("ecn"),
                role: EmailParticipantRole.TO,
                contact: {
                  id: createId("ecn"),
                  email,
                  displayName: null,
                  contactType: EmailContactType.EXTERNAL,
                  patientIds: [],
                  patients: [],
                },
              }),
            ),
          ]
        : derivedParticipants;

      // Build optimistic EmailMessage for the thread dialog
      const now = new Date().toISOString();
      const optimisticMessage: EmailMessage = {
        id: tempId,
        threadId,
        subject,
        bodyText: payload.bodyText ?? null,
        bodyHtml: payload.bodyHtml ?? null,
        snippet: payload.bodyText?.slice(0, 100) ?? null,
        status: "SENDING",
        direction: EmailDirection.OUTBOUND,
        // A reply is always conversational mail, never a blast — this drives how the
        // reader frames the body (see the outbound-full-bleed normalization).
        messageType: "CUSTOM",
        sentDate: null,
        deliveredDate: null,
        isRead: true,
        isStarred: false,
        isDraft: false,
        draftKind: null,
        parentMessageId: null,
        sentBy: null,
        participants: replyParticipants,
        attachments: [],
        openCount: 0,
        uniqueOpenCount: 0,
        clickCount: 0,
        uniqueClickCount: 0,
        firstOpenedDate: null,
        lastOpenedDate: null,
        createdDate: now,
        updatedDate: null,
      };

      // Append to thread cache
      if (previousThread) {
        queryClient.setQueryData<EmailThread>(threadKey, {
          ...previousThread,
          messages: [...previousThread.messages, optimisticMessage],
        });
      }

      if (!chatId) {
        return {
          tempId,
          previousThread,
          previousDetail: undefined,
          previousInfiniteListData: undefined,
          chatId,
          scope,
        };
      }

      // Build optimistic ChatEmailSummary for the chat timeline
      const optimisticEmailSummary: ChatEmailSummary = {
        id: tempId,
        threadId,
        subject,
        snippet: payload.bodyText?.slice(0, 100) ?? null,
        direction: EmailDirection.OUTBOUND,
        status: "SENDING",
        sentDate: null,
        createdDate: new Date().toISOString(),
      };

      // Update chat caches
      const { previousDetail, previousInfiniteListData } =
        updateChatOptimistically(
          chatId,
          (chat) => ({
            ...chat,
            emails: updateOrAddEmail(chat.emails, optimisticEmailSummary),
          }),
          (chat) => ({
            ...chat,
            latestMessageContent: subject ?? "Email reply",
            latestMessageDate: optimisticEmailSummary.createdDate,
          }),
        );

      return {
        tempId,
        previousThread,
        previousDetail,
        previousInfiniteListData,
        chatId,
        scope,
      };
    },

    onSuccess: (
      response,
      { threadId, chatId, scope = "provider" },
      context,
    ) => {
      if (!context) return;
      const { tempId } = context;

      // Two failure shapes land in onSuccess (onError never fires for either):
      // requestData swallows a failed request into a null response, and a Mailgun
      // failure comes back as a 200 SendEmailResponse with success:false and no
      // message. Roll the optimistic reply back in both cases — otherwise it is
      // stranded in SENDING forever while the composer reports the failure.
      if (!response || response.success === false) {
        rollbackOptimisticReply(threadId, scope, chatId, context);
        return;
      }

      const realId = response.messageId;

      if (realId && chatId) {
        // Swap temp ID → real ID in chat detail cache
        updateChatDetail(chatId, (chat) => {
          const webhookAlreadyArrived = chat.emails.some(
            (e) => e.id === realId,
          );
          if (webhookAlreadyArrived) {
            // Remove optimistic temp entry; real one already exists
            return {
              ...chat,
              emails: chat.emails.filter((e) => e.id !== tempId),
            };
          }
          // Swap temp → real
          return {
            ...chat,
            emails: chat.emails.map((e) =>
              e.id === tempId ? { ...e, id: realId, status: "SENT" } : e,
            ),
          };
        });
      }

      // Patch caches from the server response instead of invalidating (the
      // clinic-mailbox caching invariant: consume the response, never re-fetch).
      const serverMessage = response.message;
      if (serverMessage) {
        // Swap the optimistic temp message for the real server message; append
        // if the temp isn't present (the thread wasn't cached at mutate time).
        queryClient.setQueryData<EmailThread>(
          emailQueryKeys.thread(threadId, scope),
          (old) => {
            if (!old) return old;
            const hasTemp = old.messages.some((m) => m.id === tempId);
            return {
              ...old,
              messages: hasTemp
                ? old.messages.map((m) => (m.id === tempId ? serverMessage : m))
                : [...old.messages, serverMessage],
              sentBy: serverMessage.sentBy,
            };
          },
        );
      }

      // Patch the clinic-mailbox list row from the server response — but only for a
      // clinic-scoped reply. A provider-scoped reply (the chat email dialog, a provider-
      // only thread) returns a provider-shaped row that doesn't belong in the shared
      // clinic caches, and inserting it would leak a provider thread into clinic Sent/All.
      // A reply gives the thread its first (or newest) sent message, so it now belongs at
      // the top of Sent and All — insert it there if those lists are cached without it.
      if (scope === "clinic" && response.threadListItem) {
        patchClinicMailboxRow(response.threadListItem, {
          insertFolders: ["sent", "all"],
        });
      }
    },

    onError: (_error, { threadId, chatId, scope = "provider" }, context) => {
      if (!context) return;
      rollbackOptimisticReply(threadId, scope, chatId, context);
    },
  });
}

export function useComposeEmail() {
  return useMutation({
    mutationFn: ({
      payload,
      files,
      documentIds,
    }: {
      payload: ComposeEmailPayload;
      files?: File[];
      /** Index-aligned with `files` — the source chart document per attachment. */
      documentIds?: (string | null)[];
    }) => composeEmail(payload, files, documentIds),
    onSuccess: (result, variables) => {
      // A patient-linked send with a chart document attached is send evidence,
      // and a phlebotomy order's Sent status derives from it. Keyed on the
      // payload's patientId so mailbox sends with no patient are unaffected.
      if (result?.success && variables.payload.patientId) {
        queryClient.invalidateQueries({
          queryKey: phlebotomyOrderQueryKeys.list(variables.payload.patientId),
        });
        invalidateOpportunityContactViews();
      }
    },
  });
}

export function useForwardEmail() {
  return useMutation({
    mutationFn: ({
      threadId,
      payload,
      files,
    }: {
      threadId: string;
      payload: ForwardEmailPayload;
      files?: File[];
    }) => forwardEmail(threadId, payload, files),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: emailQueryKeys.thread(variables.threadId),
      });
      // Forwarding creates a NEW forwarded thread on the backend. In the
      // quick-send path (forward before autosave created a draft) discard() is a
      // no-op, so nothing else refetches the mailbox — invalidate the list/counts
      // here (like replies/compose do) so the forwarded email shows up in Sent.
      queryClient.invalidateQueries({ queryKey: ["email", "clinic-mailbox"] });
    },
  });
}

export function useSaveDraft() {
  return useMutation({
    mutationFn: (
      variables:
        | { draftId: string; payload: UpdateDraftPayload }
        | { draftId?: undefined; payload: CreateDraftPayload },
    ) => {
      if (variables.draftId) {
        return updateDraft(variables.draftId, variables.payload);
      }
      return createDraft(variables.payload);
    },
    onSuccess: (data, variables) => {
      // createDraft/updateDraft resolve to null on a failed request (requestData
      // swallows the error), and React Query still treats that as success. Don't
      // touch the cache as if the draft actually saved.
      if (!data) return;
      // A draft *update* returns the freshly-saved message. Patch it straight
      // into the cached thread(s) in place — no refetch. Invalidating instead
      // would serve the pre-edit cached body on reopen: the composer's one-shot
      // seed grabs the stale cache before any background refetch lands, so the
      // edit looks lost. Non-exact key patches both the provider and the
      // clinic-suffixed thread caches.
      if (variables.draftId) {
        if (data.threadId) {
          queryClient.setQueriesData<EmailThread | null>(
            { queryKey: emailQueryKeys.thread(data.threadId) },
            (old) =>
              old
                ? {
                    ...old,
                    messages: old.messages.map((message) =>
                      message.id === data.id
                        ? {
                            ...message,
                            ...data,
                            // Only the thread detail resolves a draft's parent, and it never changes.
                            parentMessageId: message.parentMessageId,
                          }
                        : message,
                    ),
                  }
                : old,
          );
        }
        // Only a standalone (NEW) draft owns its thread, so only its row is derived
        // from the draft's recipients/subject/snippet. A reply/forward draft lives in
        // a real conversation whose row still describes the last sent/received message
        // (the backend deliberately does not re-date or re-snippet that thread when a
        // reply draft is saved), so patching it here would overwrite the Inbox/Sent
        // preview with the half-written draft.
        // draftKind is nullable (drafts predating the column), and the backend treats a
        // missing kind as NEW — mirror that, or a legacy standalone draft's row would
        // freeze at its create-time state.
        if (data.draftKind && data.draftKind !== "NEW") return;
        // Patch the Drafts list row in place from the saved draft — no refetch.
        // The row is derived from the draft's latest recipients/subject/snippet,
        // and the update endpoint returns exactly those (backend update_draft
        // syncs a standalone draft's thread subject/snippet and rebuilds its
        // TO/CC/BCC participants, and the route re-loads the message with full
        // details before responding). Otherwise the row stays frozen at its
        // create-time state — e.g. a body-first draft created with no recipient
        // would show the clinic itself as the counterparty forever. fromContact
        // is left as-is: the counterparty for a draft-with-recipient resolves to
        // the external TO, and the create-time row already carries the correctly
        // clinic-masked sender.
        const nextToContacts = data.participants
          .filter((participant) => participant.role === "TO")
          .map((participant) => participant.contact);
        const nextHasAttachments = data.attachments.length > 0;
        queryClient.setQueriesData<
          InfiniteData<CursorPage<EmailThreadListItem> | null>
        >({ queryKey: ["email", "clinic-mailbox"] }, (old) => {
          // The prefix also matches the folder-counts query (a different shape);
          // only the paged thread lists have `pages`, so skip anything else.
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page) =>
              page
                ? {
                    ...page,
                    data: page.data.map((thread) =>
                      thread.id === data.threadId
                        ? {
                            ...thread,
                            subject: data.subject,
                            snippet: data.snippet,
                            toContacts: nextToContacts,
                            hasAttachments: nextHasAttachments,
                          }
                        : thread,
                    ),
                  }
                : page,
            ),
          };
        });
        return;
      }
      // First save (draft creation) adds a new message and a Drafts entry, so
      // refresh the mailbox thread list + folder counts to surface it.
      queryClient.invalidateQueries({ queryKey: ["email", "clinic-mailbox"] });
      // A reply/forward draft belongs to an open thread; invalidate its detail
      // so the thread view shows the new draft. Non-exact so both the provider
      // key and the clinic-suffixed key are covered.
      if (data.threadId) {
        queryClient.invalidateQueries({
          queryKey: emailQueryKeys.thread(data.threadId),
        });
      }
    },
  });
}

export function useDeleteDraft() {
  return useMutation({
    mutationFn: (draftId: string) => deleteDraft(draftId),
    onSuccess: () => {
      // The delete route returns 204 No Content, so deleteDraft resolves to null
      // on success — indistinguishable at this layer from a swallowed failure. So
      // always invalidate: after a real delete this refreshes the Drafts list and
      // any open thread; after a (rare) failed delete it just refetches the
      // still-present draft, which is also correct.
      queryClient.invalidateQueries({ queryKey: ["email", "clinic-mailbox"] });
      // A deleted in-thread reply/forward draft is still cached in the open
      // thread detail, so the draft row would linger until a manual refetch.
      // Invalidate thread detail (non-exact: covers the provider and
      // clinic-suffixed keys) so the deleted draft disappears immediately.
      queryClient.invalidateQueries({
        queryKey: [...emailQueryKeys.all, "thread"],
      });
    },
  });
}

export function useSendDraft() {
  return useMutation({
    mutationFn: (draftId: string) => sendDraft(draftId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: emailQueryKeys.all,
      });
    },
  });
}

export function useEmailSignature() {
  return useQuery({
    queryKey: emailQueryKeys.signature,
    queryFn: getEmailSignature,
  });
}

export function useSaveEmailSignature() {
  return useMutation({
    mutationFn: (payload: SaveEmailSignaturePayload) =>
      saveEmailSignature(payload),
    // Returned, so the save settles only once the editor has its document back:
    // useContentVersion holds one own save at a time.
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: emailQueryKeys.signature,
      }),
  });
}

export function useEmailHeader(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: emailQueryKeys.header,
    queryFn: getEmailHeader,
    enabled: options?.enabled ?? true,
  });
}

export function useSaveEmailHeader() {
  return useMutation({
    mutationFn: (payload: SaveEmailHeaderPayload) => saveEmailHeader(payload),
    // Returned, so the save settles only once the editor has its document back:
    // useContentVersion holds one own save at a time.
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: emailQueryKeys.header,
      }),
  });
}
